import React, { useState } from "react";
import MemberLayout from "../components/MemberLayout";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AccountSettings() {
  const { user, checkAuth } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const savePassword = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { toast.error("Passwords don't match."); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/set-password", { password: pw });
      await checkAuth();
      setPw(""); setPw2("");
      toast.success("Password saved. You can now sign in either way.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save password.");
    } finally {
      setSavingPw(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name can't be empty."); return; }
    setSaving(true);
    try {
      await api.patch("/auth/profile", { name: name.trim() });
      await checkAuth();
      toast.success("Name updated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <MemberLayout>
      <div className="haruki-fade-in max-w-xl">
        <h1 className="font-heading text-2xl font-bold sm:text-3xl">Account</h1>
        <p className="mt-2 text-sm text-muted-foreground">Manage how Haruki addresses you.</p>

        <Card className="mt-6">
          <CardHeader><CardTitle className="font-heading text-base">Your name</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={save} className="space-y-4" data-testid="account-settings-form">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Full name
                </Label>
                <Input data-testid="account-name-input" value={name}
                       onChange={(e) => setName(e.target.value)} placeholder="First Last" maxLength={80} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Email
                </Label>
                <Input value={user?.email || ""} disabled className="opacity-60" data-testid="account-email-readonly" />
              </div>
              <Button data-testid="account-save-button" type="submit" disabled={saving}
                      style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}>
                {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</> : "Save changes"}
              </Button>
            </form>
          </CardContent>
        </Card>
        <Card className="mt-6">
          <CardHeader><CardTitle className="font-heading text-base">Password (optional)</CardTitle></CardHeader>
          <CardContent>
            <p className="mb-4 text-xs text-muted-foreground">
              {user?.has_password
                ? "You have a password set. You can sign in with it or with an email link — your choice."
                : "Add an optional password as a backup way in. Email sign-in links always keep working."}
            </p>
            <form onSubmit={savePassword} className="space-y-4" data-testid="password-form">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  {user?.has_password ? "New password" : "Password"}
                </Label>
                <Input data-testid="password-input" type="password" value={pw}
                       onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters"
                       autoComplete="new-password" maxLength={128} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Confirm password
                </Label>
                <Input data-testid="password-confirm-input" type="password" value={pw2}
                       onChange={(e) => setPw2(e.target.value)} placeholder="Repeat it"
                       autoComplete="new-password" maxLength={128} />
              </div>
              <Button data-testid="password-save-button" type="submit" disabled={savingPw}
                      style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}>
                {savingPw ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</>
                          : user?.has_password ? "Change password" : "Set password"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </MemberLayout>
  );
}
